import { Fragment } from 'react';
import { Clock3, Mail, Users } from 'lucide-react';
import { useI18n } from '../i18n';
import type { RegistrationAccessStatus } from '../lib/registerApi';

const emailStatuses = new Set(['sent', 'partial', 'disabled', 'redirected', 'failed', 'no_managers', 'unknown']);

const maskManagerName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.map((part, index) => index === 0 && parts.length > 1 ? part : `${Array.from(part)[0]}***`).join(' ');
};

export default function RegistrationAccessNotice({ status, overlay = false, standalone = false }: { status: RegistrationAccessStatus; overlay?: boolean; standalone?: boolean }) {
  const { t } = useI18n();
  if (!status.pending) return null;
  const rejected = status.decision === 'rejected';
  const panel = overlay || standalone;
  const Heading = standalone ? 'h1' : 'h2';
  const emailStatus = emailStatuses.has(status.manager_notification_status) ? status.manager_notification_status : 'unknown';
  const renderContacts = (text: string) => text.split(/(\{manager\}|\{support\})/).map((part, index) => (
    <Fragment key={index}>{part === '{support}'
      ? 'Support'
      : part === '{manager}'
        ? t('register_access_manager_contact')
        : part}</Fragment>
  ));
  return <section role="status" className={standalone ? 'w-full max-w-xl border-y border-gray-200 bg-white px-5 py-6 text-sm text-gray-600 sm:p-8' : overlay
    ? 'w-full max-w-lg rounded-2xl border border-gray-200/80 bg-white p-6 text-sm text-gray-600 shadow-xl shadow-gray-900/10 sm:p-8'
    : 'mb-5 border-l-2 border-primary-500 bg-white px-4 py-3 text-sm text-gray-700'}>
    {panel && <div className={standalone ? 'mb-5 text-gray-500' : 'mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-600'}><Clock3 size={24} aria-hidden="true" /></div>}
    <Heading className={panel ? "text-xl font-semibold tracking-tight text-gray-950" : "font-semibold text-gray-950"}>{t(rejected ? 'register_access_rejected_title' : 'register_access_pending_title')}</Heading>
    <p className={panel ? "mt-3 leading-6" : "mt-1"}>{t(rejected ? 'register_access_rejected_description' : standalone ? 'register_access_waiting_description' : 'register_access_pending_description', { club: status.club_name, team: status.team_name })}</p>
    <div className={panel ? 'mt-6 space-y-4 border-t border-gray-100 pt-5' : 'mt-2 space-y-1'}>
      <div className="flex items-start gap-3">
        {panel && <Users size={18} className="mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />}
        <p className="min-w-0 break-words leading-6">{status.club_managers.length
          ? <>{t('register_access_managers', { names: '' })}{status.club_managers.map((name, index) => <Fragment key={index}>{index > 0 && ', '}{maskManagerName(name)}</Fragment>)}</>
          : renderContacts(t('register_access_managers_unknown'))}</p>
      </div>
      {!rejected && <div className="flex items-start gap-3">
        {panel && <Mail size={18} className="mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />}
        <p className="min-w-0 leading-6">{renderContacts(t(`register_access_email_${emailStatus}`))}</p>
      </div>}
    </div>
    {status.expired && !status.review_in_portal && !rejected && <p className="mt-1">{t('register_access_expired')}</p>}
  </section>;
}
